<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <div>
      <v-row class="ma-0 mb-3">
        <h2>{{title}}</h2>
        <v-btn class="ml-2" color="primary" v-on:click="createNewShow">+ New Show</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" v-on:click="applyFilter">Apply Filters</v-btn>&nbsp;
        <v-btn color="secondary" v-on:click="clearAllFilters">Clear</v-btn>
      </v-row>
    </div>
    <PaginationSelect
      :totalCount="paginatedResponse.totalCount"
      :page="options.page"
      :size="options.itemsPerPage"
      v-on:load="load"
      v-on:changePage="options.page = $event"
      v-on:changeSize="options.itemsPerPage = $event"
    />
    <v-row>
      <v-alert width="100%" class="mr-5 ml-5" v-model="isError" dismissible type="error"> {{ errorMessage }}</v-alert>
    </v-row>
    <v-data-table
      dense
      disable-sort
      hide-default-footer
      class="show-search-table elevation-1"
      :loading="loading"
      :headers="headers"
      :options.sync="options"
      :items="paginatedResponse.data"
      :server-items-length="paginatedResponse.totalCount"
    >
        <template v-slot:header.showId="{ header }">
            <v-text-field hide-details
              v-model="searchParams.showId"
              class="mt-0 pt-0 mb-2"
              label="Show ID"
              clearable
              @click:clear="searchParams.showId = ''"
              v-on:keyup.enter="applyFilter"
            />
        </template>
        <template v-slot:header.affiliateId="{ header }">
            <v-text-field hide-details
              class="mt-0 pt-0 mb-2"
              v-model="searchParams.affiliateId"
              label="Affiliate"
              clearable
              @click:clear="searchParams.affiliateId = ''"
              v-on:keyup.enter="applyFilter"
            />
        </template>
        <template v-slot:header.affiliateName="{ header }">
            <v-text-field hide-details
              class="mt-0 pt-0 mb-2"
              v-model="searchParams.affiliateName"
              label="Affiliate Name"
              clearable
              @click:clear="searchParams.affiliateName = ''"
              v-on:keyup.enter="applyFilter"
            />
        </template>
        <template v-slot:header.judgeId="{ header }">
          <v-text-field hide-details
            v-model="searchParams.judgeId"
            class="mt-0 pt-0 mb-2"
            label="Judge Id"
            clearable
            @click:clear="searchParams.judgeId = ''"
            v-on:keyup.enter="applyFilter"
          />
        </template>
        <template v-slot:header.showType="{ header }">
          <v-combobox v-model="Show_Types"
                      :items="ShowTypes"
                      class="mt-3"
                      label="Show Types"
                      clearable
                      multiple
                      outlined
                      dense
                      small-chips>
          </v-combobox>
        </template>
        <template v-slot:header.showStartDate="{ header }">
            <datepicker
              :date="searchParams.showDateFrom"
              label="From"
              v-on:updateDates="updateDates($event, 'showDateFrom')"
              v-on:enter="updateDateSearch($event, 'showDateFrom')"
            />
        </template>
        <template v-slot:header.showEndDate="{ header }">
            <datepicker
              :date="searchParams.showDateTo"
              label="To"
              v-on:updateDates="updateDates($event, 'showDateTo')"
              v-on:enter="updateDateSearch($event, 'showDateTo')"
            />
        </template>

        <template v-slot:item.showId="{ item }">
            <router-link :to="`/admin/shows/${item.showId}`" target="_blank">
                {{ item.showId }}
            </router-link>
        </template>
        <template v-slot:item.affiliateId="{ item }">
            <router-link v-if="item.affiliateId" :to="`/admin/affiliates/${item.affiliateId}`" target="_blank">
                {{ item.affiliateId }}
            </router-link>
        </template>
        <template v-slot:item.affiliateName="{ item }">
            <router-link v-if="item.affiliateName" :to="`/admin/affiliates/${item.affiliateId}`" target="_blank">
               {{item.affiliateName}}
            </router-link>
        </template>
        <template v-slot:item.showType="{ item }">
            <span>{{ item.showType }}</span>
        </template>
        <template v-slot:item.showStartDate="{ item }">
            <localized-date :date="item.showStartDate" />
        </template>
        <template v-slot:item.showEndDate="{ item }">
            <localized-date :date="item.showEndDate" />
        </template>
    </v-data-table>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import Datepicker from "@/components/common/inputs/DatepickerFormatted.vue";
import PaginationSelect from "@/components/common/PaginationSelect.vue";

import showService from "@/services/admin/showService";
import { getErrorMessage } from "@/services/errorHandler";

import { UserInfo } from "@/types/User";
import { MetaNames } from "@/constants";
import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";

export default Vue.extend({
  name: "ShowList",
  components: {
    Datepicker,
    PaginationSelect
  },
  data: function() {
    return {
      title: MetaNames.AdminShows,
      loading: false,
      isError: false,
      errorMessage: "",
      paginatedResponse: {} as PaginatedResponse<object>,
      searchParams: {
        showId: "",
        affiliateId: "",
        affiliateName: "",
        showTypes: "",
        showDateFrom: "",
        showDateTo: "",
        judgeId: "",
      },
      options: {} as PaginationOptions,
      headers: [
        { text: "", align: "left", value: "showId", width: "8%" },
        { text: "", align: "left", value: "affiliateId", width: "8%" },
        { text: "", align: "left", value: "affiliateName", width: "24%" },
        { text: "", align: "left", value: "showStartDate", width: "13%" },
        { text: "", align: "left", value: "showEndDate", width: "13%" },
        { text: "", align: "left", value: "judgeId", width: "10%" },
        { text: "", align: "left", value: "showType", width: "24%" },
      ],
      footerProps: { 'items-per-page-options': [10, 20, 30, 40, 50, 100] },
      currentIsAscaIdx: 0,
      ShowTypes: [
        "AGILITY",
        "CONFORMATION",
        "CONFORMATION AND OBEDIENCE",
        "DOCK JUMPING",
        "FARM TRIAL",
        "JUNIORS",
        "JUNIORS & OBEDIENCE",
        "NATIONAL SPECIALTY",
        "NATIONAL SPECIALTY - AGILITY",
        "NATIONAL SPECIALTY - STOCK DOG",
        "NATIONAL SPECIALTY PRE-AGILITY",
        "NATIONAL SPECIALTY PRE-SHOW",
        "NATIONAL SPECIALTY PRE-TRIAL",
        "OBEDIENCE",
        "RALLY",
        "RANCH TRIAL",
        "SCENT DETECTION",
        "STOCK TRIAL",
        "TRACKING"
        
      ],
      Show_Types: [],
      menu: false,
      dates: [] as string[],
    };
  },
  created: function() {
    this.load();
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    }
  },
  methods: {
    createNewShow: function () {
      this.$navigate({ path: '/admin/shows' });
    },
    clearAllFilters: function() {
      this.options.page = 1;
      this.options.itemsPerPage = 10;
      this.dates = [];
      this.Show_Types= [],
      this.searchParams = {
        showId: "",
        affiliateName:"",
        showTypes: "",
        showDateFrom: "",
          showDateTo: "",
        affiliateId:"",
        judgeId: "",
      };
    },
    updateDates: function (val: any, key: string) {
      (this as any).searchParams[key] = val.date;
    },
    updateDateSearch: async function(val: any, key: string) {
      await this.updateDates(val, key);
      this.applyFilter();
    },
    load: async function () {
      this.loading = true;

      const {
        page,
        itemsPerPage,
      } = this.options;

      try {
        this.searchParams.showTypes = this.Show_Types.join('|');

        const response = await showService.getShowsList({
          ...this.options,
          page: (page ?? 1) - 1,
          itemsPerPage: itemsPerPage ?? 10
        }, this.searchParams);

        this.loading = false;
        this.paginatedResponse = response;
      } catch (ex: any) {
        this.loading = false;
        this.isError = true;

        this.errorMessage = getErrorMessage(ex);
      }
    },
    applyFilter() {
      this.options.page = 1;
      this.options.itemsPerPage = 10;

      this.load();
    }
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
  },
});
</script>

<style scoped>
::v-deep .show-search-table thead .v-label,
::v-deep .show-search-table thead input,
::v-deep .show-search-table thead .v-select__selection {
  font-size: 13px;
}

::v-deep .show-search-table thead .v-input {
  position: relative;
}

::v-deep .show-search-table thead .v-input__prepend-outer {
  position: absolute;
  left: -28px;
  margin-right: 0;
}

::v-deep .show-search-table th,
::v-deep .show-search-table td {
  padding-left: 4px;
  padding-right: 4px;
}

::v-deep .show-search-table th:first-child,
::v-deep .show-search-table td:first-child {
  padding-left: 8px;
}

::v-deep .show-search-table th:last-child,
::v-deep .show-search-table td:last-child {
  padding-right: 8px;
}
</style>
